import React from "react";
import { Box, Typography, CircularProgress, Alert, Chip, CardContent } from "@mui/material";
import { StyledCard, HeaderCell, DataCell } from "./styled";
import { MobileCard } from "../styled";

const QueryHistoryCard = ({ osmQueries, loadingQueries, formatDistance, isMobile, ...cardProps }) => (
  <StyledCard {...cardProps}>
    <CardContent>
      <CardContentBody
        osmQueries={osmQueries}
        loadingQueries={loadingQueries}
        formatDistance={formatDistance}
        isMobile={isMobile}
      />
    </CardContent>
  </StyledCard>
);

const CardContentBody = ({ osmQueries, loadingQueries, formatDistance, isMobile }) => (
  <Box component="div" sx={{ width: "100%" }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h6">
        OSM Query History ({osmQueries.length} queries)
      </Typography>
    </Box>

    {loadingQueries ? (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography>Loading query history...</Typography>
      </Box>
    ) : osmQueries.length > 0 ? (
      isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 400, overflowY: "auto" }}>
          {osmQueries.map((query) => (
            <MobileCard key={query.id}>
              <div className="card-header">
                <strong>{query.location}</strong>
                <Chip
                  size="small"
                  label={query.inserted_into_database ? "Inserted" : "Pending"}
                  color={query.inserted_into_database ? "success" : "default"}
                  variant="outlined"
                />
              </div>
              <div className="card-row">
                <span className="card-label">Radius</span>
                <span className="card-value">{formatDistance(query.radius_meters)}</span>
              </div>
              <div className="card-row">
                <span className="card-label">Results</span>
                <span className="card-value" style={{ fontWeight: 500 }}>
                  {query.number_of_results}
                </span>
              </div>
              <div className="card-row">
                <span className="card-label">Date</span>
                <span className="card-value muted">{formatTimestamp(query.query_date)}</span>
              </div>
              <div className="card-row">
                <span className="card-label">By</span>
                <span className="card-value muted" style={{ fontSize: "0.75rem" }}>
                  {query.created_by_email || query.created_by_user || "Unknown"}
                </span>
              </div>
              {query.inserted_into_database && query.inserted_date && (
                <div className="card-row">
                  <span className="card-label">Inserted</span>
                  <span className="card-value muted">{formatTimestamp(query.inserted_date)}</span>
                </div>
              )}
            </MobileCard>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            overflowX: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            width: "100%"
          }}
        >
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto"
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2.2fr 1.8fr 0.9fr 0.8fr 1.5fr 1.1fr 1.5fr",
                gap: "1px",
                backgroundColor: "#f5f5f5",
                width: "100%"
              }}
            >
              <HeaderCell>Location</HeaderCell>
              <HeaderCell>Requested By</HeaderCell>
              <HeaderCell>Radius</HeaderCell>
              <HeaderCell>Results</HeaderCell>
              <HeaderCell>Query Date</HeaderCell>
              <HeaderCell>Inserted to DB</HeaderCell>
              <HeaderCell>Inserted Date</HeaderCell>

              {osmQueries.map((query) => (
                <React.Fragment key={query.id}>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      {query.location}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                      {query.created_by_email || query.created_by_user || "Unknown"}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                      {formatDistance(query.radius_meters)}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.85rem", fontWeight: "medium" }}>
                      {query.number_of_results}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      {formatTimestamp(query.query_date)}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Chip
                      size="small"
                      label={query.inserted_into_database ? "Yes" : "No"}
                      color={query.inserted_into_database ? "success" : "default"}
                      variant="outlined"
                    />
                  </DataCell>
                  <DataCell>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      {formatTimestamp(query.inserted_date)}
                    </Typography>
                  </DataCell>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      )
    ) : (
      <Alert severity="info">No query history yet. Run a query to see it here.</Alert>
    )}
  </Box>
);

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleString();
  }
  if (typeof timestamp === "number") {
    return new Date(timestamp).toLocaleString();
  }
  return "—";
};

export default QueryHistoryCard;
